// 📁 src/lightversion/PurposeConfirmLite.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { useLite } from "./context/LiteContext";
import LiteDayGate from "./LiteDayGate";
import LiteDayLayout from "./LiteDayLayout";
import ExperienceItem from "./components/ExperienceItem";
import LiteLearningBlock from "./components/LiteLearningBlock"; // ✅ NEW



const PurposeConfirmLite = () => {
  const navigate = useNavigate();
  const { 
  liteJourney,
  updatePurpose,
  markDayComplete,
  entryDecided,
  loading: liteLoading
} = useLite();

  /* =====================================================
     🔑 LOCAL STATE (CRITICAL)
  ===================================================== */
  const [reflectionSummary, setReflectionSummary] = useState("");
  const [purpose, setPurpose] = useState("");
  const [justification, setJustification] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingRefine, setLoadingRefine] = useState(false);

  /* =====================================================
     🧪 DEBUG
  ===================================================== */
  useEffect(() => {
    console.log("[Day2] liteJourney snapshot:", liteJourney);
  }, [liteJourney]);

  /* =====================================================
     🔑 HYDRATE REFLECTION SUMMARY
  ===================================================== */
  useEffect(() => {
    if (liteJourney?.day1Summary) {
      setReflectionSummary(liteJourney.day1Summary);
    }
  }, [liteJourney?.day1Summary]);

  /* =====================================================
     🔒 ENTRY GUARD
  ===================================================== */
  useEffect(() => {

  if (liteLoading) return;

  if (!entryDecided && window.location.pathname !== "/lite/welcome") {
  navigate("/lite/welcome", { replace: true });
  }

  }, [entryDecided, liteLoading, navigate]);

  /* =====================================================
     🔁 DAY ORDER GUARDS
  ===================================================== */
  useEffect(() => {
    if (!liteJourney?.completedDays?.includes(1)) {
      navigate("/lite/mentor", { replace: true });
      return;
    }

    if (liteJourney?.completedDays?.includes(2)) {
      navigate("/lite/idea", { replace: true });
    }
  }, [liteJourney, navigate]);

  /* =====================================================
     🧠 GENERATE PURPOSE
  ===================================================== */
  const handleGeneratePurpose = async () => {
    if (!reflectionSummary || loadingGenerate) return;

    setLoadingGenerate(true);
    try {
      const res = await axios.post("/lite/purpose/draft", {
        reflection: reflectionSummary,
      });

      setPurpose(res.data?.purpose?.statement || "");
      setJustification(res.data?.purpose?.justification || "");
    } catch (err) {
      console.error("[Day2] Purpose generation failed", err);
    } finally {
      setLoadingGenerate(false);
    }
  };

  /* =====================================================
     ✨ REFINE PURPOSE
  ===================================================== */
  const refineWithAI = async () => {
    if (!purpose.trim()) return;

    setLoadingRefine(true);
    try {
      const res = await axios.post("/lite/refine-purpose", { purpose });
      setSuggestions(res.data?.suggestions || []);
    } finally {
      setLoadingRefine(false);
    }
  };

  /* =====================================================
     ✅ COMPLETE DAY 2
  ===================================================== */
  const handleCompleteDay = () => {
    if (!purpose.trim()) return;

    updatePurpose({ statement: purpose.trim(), refinedByAI: false });
    markDayComplete();
    navigate("/lite/idea");
  };

  return (
    <LiteDayGate allow={["WHY_IT_MATTERS", "PURPOSE_CLARIFICATION"]}>
      <LiteDayLayout
        day={2}
        allowAction={!!purpose.trim()}
        onComplete={handleCompleteDay}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* EXPERIENCE CONTEXT */}
          <Box sx={{ mb: 4 }}>
            <Stack spacing={4}>
              <ExperienceItem
                icon="🎯"
                title="Purpose is not a solution"
                text="Founders often confuse purpose with product.But purpose is bigger—it’s the impact you seek.Your startup is just one way to deliver it."
              />
              <ExperienceItem
                icon="🧠"
                title="Anchor it in emotion"
                text="Ideas fade, but emotions endure.Your strongest purpose is born from what you’ve lived.Founders who feel deeply build ventures that matter."
              />
              <ExperienceItem
                icon="🌱"
                title="Let ideas come later"
                text="Clarity is the seed—ideas are the branches.Without clarity, ideas scatter.Strong startups grow from conviction first, creativity second."
              />
            </Stack>
          </Box>

          {/* 🧠 INLINE LITE LEARNING (OPTIONAL) */}
          <LiteLearningBlock day={2} />

          {/* REFLECTION SUMMARY */}
          <Card sx={{ mb: 4, backgroundColor: "#f9fafb", mt: 4 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Reflection Summary (from Day 1)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {reflectionSummary || "Loading your reflection…"}
              </Typography>
            </CardContent>
          </Card>

          {/* GENERATE CTA */}
          <Box sx={{ mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGeneratePurpose}
              disabled={!reflectionSummary || loadingGenerate}
            >
              {loadingGenerate
                ? "Generating Purpose…"
                : "Generate My Most Transformational Purpose"}
            </Button>
          </Box>

          {justification && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Why this purpose fits you
              </Typography>
              <Typography variant="body2">{justification}</Typography>
            </Alert>
          )}

          {purpose && (
            <Card>
              <CardContent>
                <Stack spacing={3}>
                  <TextField
                    label="Your Purpose (Day 2 outcome)"
                    multiline
                    minRows={3}
                    fullWidth
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                  />

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={refineWithAI}
                      disabled={loadingRefine}
                    >
                      ✨ Help me refine
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleCompleteDay}
                    >
                      Complete Day 2 →
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}

          {suggestions.length > 0 && (
            <>
              <Divider sx={{ my: 4 }} />
              <Stack spacing={2}>
                {suggestions.map((s, idx) => (
                  <Card key={idx} onClick={() => setPurpose(s)}>
                    <CardContent>
                      <Typography>{s}</Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </LiteDayLayout>
    </LiteDayGate>
  );
};

export default PurposeConfirmLite;
